import React, { useEffect, useId, useRef, useState } from 'react';
import { Copy, Check, Maximize2, Minimize2, Code, AlertCircle, Workflow } from 'lucide-react';
import DOMPurify from 'dompurify';
import type { ChatDiagram } from '../../types';
import { MermaidFullscreenViewer } from './MermaidFullscreenViewer';

interface MermaidDiagramCardProps {
  diagram: ChatDiagram;
  isUser?: boolean;
}

export const MermaidDiagramCard: React.FC<MermaidDiagramCardProps> = ({ diagram, isUser }) => {
  const uniqueId = useId().replace(/:/g, '_');
  const containerRef = useRef<HTMLDivElement>(null);
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [copied, setCopied] = useState<boolean>(false);
  const [showRawCode, setShowRawCode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  useEffect(() => {
    let isCancelled = false;

    async function renderMermaid() {
      setIsLoading(true);
      setRenderError(null);

      try {
        // Dynamically import mermaid (bundled as a separate lazy chunk by Vite)
        const mod = await import('mermaid');
        const mermaid = mod.default || mod;

        if (!mermaid) {
          throw new Error('Mermaid rendering engine is not available.');
        }

        const isDark = document.documentElement.classList.contains('dark');

        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: isDark ? 'dark' : 'default',
          suppressErrorRendering: true,
          // HTML labels live in <foreignObject>, which the SVG-only DOMPurify profile below strips (blank boxes).
          htmlLabels: false,
          flowchart: { htmlLabels: false },
          maxTextSize: 20000,
          maxEdges: 500,
        });

        const renderElementId = `mermaid_${uniqueId}_${Date.now()}`;
        const { svg } = await mermaid.render(renderElementId, diagram.mermaid);

        const sanitizedSvg = DOMPurify.sanitize(svg, {
          USE_PROFILES: { svg: true, svgFilters: true },
        });

        if (!isCancelled) {
          setSvgContent(sanitizedSvg);
          setIsLoading(false);
        }
      } catch (error) {
        if (!isCancelled) {
          const errMsg = error instanceof Error ? error.message : String(error);
          setRenderError(errMsg);
          setIsLoading(false);
        }
      }
    }

    void renderMermaid();

    return () => {
      isCancelled = true;
    };
  }, [diagram.mermaid, uniqueId]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(diagram.mermaid);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore clipboard error
    }
  };

  const cardBorderClass = isUser
    ? 'border-blue-300/60 bg-blue-950/20 text-white'
    : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 text-slate-800 dark:text-slate-100';

  return (
    <div
      className={`my-3 overflow-hidden rounded-xl border shadow-sm transition-all ${cardBorderClass}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200/80 bg-slate-50/70 px-3.5 py-2 text-xs font-medium dark:border-slate-800/80 dark:bg-slate-800/40">
        <div className="flex items-center gap-2 overflow-hidden">
          <Workflow size={15} className="shrink-0 text-teal-600 dark:text-teal-400" />
          <span className="truncate font-semibold text-slate-900 dark:text-slate-100">
            {diagram.title || 'Diagram'}
          </span>
          <span className="shrink-0 rounded-full bg-slate-200/70 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-600 dark:bg-slate-700/70 dark:text-slate-300">
            {diagram.diagramType}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowRawCode((prev) => !prev)}
            title={showRawCode ? 'Show Diagram' : 'View Mermaid Source'}
            className="flex h-7 items-center gap-1 rounded-md px-2 text-[11px] text-slate-600 transition-colors hover:bg-slate-200/60 dark:text-slate-300 dark:hover:bg-slate-700/60"
          >
            <Code size={13} />
            <span className="hidden sm:inline">{showRawCode ? 'Diagram' : 'Code'}</span>
          </button>

          <button
            type="button"
            onClick={handleCopy}
            title="Copy Mermaid source"
            className="flex h-7 w-7 items-center justify-center rounded-md text-slate-600 transition-colors hover:bg-slate-200/60 dark:text-slate-300 dark:hover:bg-slate-700/60"
          >
            {copied ? <Check size={13} className="text-emerald-500" /> : <Copy size={13} />}
          </button>

          {!renderError && (
            <button
              type="button"
              onClick={() => setIsFullscreen((prev) => !prev)}
              title={isFullscreen ? 'Exit Fullscreen' : 'View Fullscreen'}
              className="flex h-7 w-7 items-center justify-center rounded-md text-slate-600 transition-colors hover:bg-slate-200/60 dark:text-slate-300 dark:hover:bg-slate-700/60"
            >
              {isFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
            </button>
          )}
        </div>
      </div>

      {/* Description if present */}
      {diagram.description && (
        <div className="border-b border-slate-100 bg-slate-50/40 px-3.5 py-1.5 text-xs text-slate-500 dark:border-slate-800/40 dark:bg-slate-900/30 dark:text-slate-400">
          {diagram.description}
        </div>
      )}

      {/* Body content */}
      <div className="p-3">
        {isLoading && (
          <div className="flex h-32 items-center justify-center text-xs text-slate-400">
            <span className="inline-block animate-pulse">Rendering diagram...</span>
          </div>
        )}

        {showRawCode ? (
          <pre className="max-h-80 overflow-auto rounded-lg bg-slate-950 p-3 font-mono text-[11px] leading-relaxed text-slate-200">
            <code>{diagram.mermaid}</code>
          </pre>
        ) : renderError ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
            <div className="mb-2 flex items-center gap-1.5 font-semibold text-amber-900 dark:text-amber-200">
              <AlertCircle size={15} />
              <span>Unable to render diagram preview</span>
            </div>
            <p className="mb-2 text-[11px] opacity-90">{renderError}</p>
            <pre className="max-h-60 overflow-auto rounded bg-slate-900 p-2.5 font-mono text-[11px] text-slate-200">
              <code>{diagram.mermaid}</code>
            </pre>
          </div>
        ) : (
          <div
            ref={containerRef}
            className="mermaid-rendered-container flex justify-center overflow-x-auto py-2 [&_svg]:h-auto [&_svg]:max-w-full"
          >
            {svgContent ? (
              <div
                className="flex w-full justify-center"
                dangerouslySetInnerHTML={{ __html: svgContent }}
              />
            ) : null}
          </div>
        )}
      </div>

      {isFullscreen && svgContent && (
        <MermaidFullscreenViewer
          title={diagram.title || 'Diagram'}
          svg={svgContent}
          onClose={() => setIsFullscreen(false)}
        />
      )}
    </div>
  );
};
